import VueModel from '@cognitoforms/vuemodel';
import { FormsModel } from '../framework/forms-model';
import type { FormSession } from './form-session';
import type { JsonErrorInfo } from './custom-response-error';
import { CustomResponseError } from './custom-response-error';
import ToastMessage from '../components/ToastMessage';
import { getSiteUrl } from 'src/util/site-url';
import { hasInnerError } from 'src/framework/logging/error';

export interface HttpResponse<T = any> {
	data: T;
	status: number;
	statusText: string;
	headers: Record<string, string>;
	config: HttpRequestConfig;
}

export interface HttpRequestConfig {
	baseURL?: string;
	url?: string;
	params?: Record<string, any>;
	headers?: Record<string, any>;
	method?: string;
	data?: any;
	passive?: boolean;
	responseType?: 'json' | 'text' | 'blob';
	timeout?: number;
	signal?: AbortSignal;
	onUploadProgress?: (event: { loaded: number; total: number; percent: number; }) => void;
}

export interface ServiceRequestOptions extends HttpRequestConfig {
	endpoint: string;
	data?: any;
	passive?: boolean;
}

export class HttpError extends Error {
	isHttpError = true;
	code?: string;
	config?: HttpRequestConfig;
	response?: HttpResponse;

	constructor(message: string, config?: HttpRequestConfig, response?: HttpResponse, code?: string) {
		super(message);
		this.name = 'HttpError';
		this.config = config;
		this.response = response;
		this.code = code;
	}
}

export type ServiceRequestError = Error | HttpError | RequestCancelledError | CustomResponseError;

export type ServiceRequestResult = {
	response?: HttpResponse;
	error?: ServiceRequestError;
};

export class RequestCancelledError extends Error {
	innerError?: Error;
	constructor(innerError?: Error) {
		super('cancelled');
		this.innerError = innerError;
	}
}

function ensureError(err: unknown): Error {
	return err instanceof Error ? err : new Error(String(err));
}

export class CaptchaError extends Error {
	innerError?: Error;
	constructor(innerError?: Error) {
		super('Captcha validation required.');
		this.innerError = innerError;
	}
}

export class InvalidSharedLinkError extends Error {
	notAvailableMessage?: string;
	innerError?: Error;
	constructor(notAvailableMessage?: string, innerError?: Error) {
		super('Invalid Shared Link');
		this.notAvailableMessage = notAvailableMessage;
		this.innerError = innerError;
	}
}

export class PublicLinksDisabledError extends Error {
	innerError?: Error;
	constructor(innerError?: Error) {
		super('Public Links Disabled');
		this.innerError = innerError;
	}
}

export class StripePaymentIntentError extends Error {
	innerError?: Error;
	constructor(innerError?: Error) {
		super('Stripe PaymentIntent Failure');
		this.innerError = innerError;
	}
}

export class InternalServerError extends Error {
	innerError?: Error;
	constructor(innerError?: Error) {
		super('Internal server error.');
		this.innerError = innerError;
	}
}

export class ForbiddenError extends Error {
	innerError?: Error;
	constructor(innerError?: Error) {
		super('You do not have permission to edit this entry.');
		this.innerError = innerError;
	}
}

export class ViewAsUserError extends Error {
	innerError?: Error;
	constructor(innerError?: Error) {
		super('View As User');
		this.innerError = innerError;
	}
}

function isJsonErrorInfo(data: any): data is JsonErrorInfo {
	return !!data
		&& typeof data === 'object'
		&& Object.prototype.hasOwnProperty.call(data, 'Type')
		&& Object.prototype.hasOwnProperty.call(data, 'Message')
		&& Object.prototype.hasOwnProperty.call(data, 'Data');
}

function hasErrorField(data: unknown): data is { error: string; message?: string; } {
	return !!data && typeof data === 'object' && typeof (data as { error?: unknown; }).error === 'string';
}

export function isHttpError(error: unknown): error is HttpError {
	return error instanceof Error && (error as any).isHttpError === true;
}

export function getHttpError(error: Error): HttpError | undefined {
	if (isHttpError(error))
		return error;
	if (hasInnerError(error)) {
		const error2 = error.innerError;
		if (isHttpError(error2))
			return error2;
		if (error2 instanceof CustomResponseError) {
			const error3 = error2.innerError;
			if (isHttpError(error3))
				return error3;
		}
	}
}

export class BaseService {
	protected readonly apiKey: string;
	protected sessionToken: string | undefined;
	protected readonly baseUrl: string;
	protected readonly client: { get<T>(url: string): Promise<{ data: T }>; };

	constructor(apiKey: string, sessionToken?: string, baseUrl?: string) {
		this.apiKey = apiKey;
		this.sessionToken = sessionToken;
		this.baseUrl = baseUrl || getSiteUrl() || window.location.origin;
		this.client = {
			get: async <T>(url: string) => {
				const response = await this.sendFetchRequest({ url, method: 'get' });
				return { data: response.data as T };
			}
		};
	}

	/**
	 * Generates the URI that the service would use to make a request with the provided configuration.
	 * @param config request configuration used to generate a URI.
	 */
	protected getUri(config: HttpRequestConfig) {
		const url = config.url || '';
		const baseUrl = config.baseURL || this.baseUrl;
		const absoluteUrl = new URL(url, baseUrl);

		if (config.params) {
			for (const [key, value] of Object.entries(config.params)) {
				if (value === undefined || value === null)
					continue;

				if (Array.isArray(value)) {
					for (const item of value) {
						absoluteUrl.searchParams.append(key, BaseService.serializeParamValue(item));
					}
				}
				else {
					absoluteUrl.searchParams.append(key, BaseService.serializeParamValue(value));
				}
			}
		}

		return absoluteUrl.toString();
	}

	/**
	 * Use custom serialization on model instances
	 */
	private static serializeParamValue(value: any) {
		if (value instanceof Date)
			return value.toISOString();
		if (typeof value === 'object')
			return JSON.stringify(value);
		return String(value);
	}

	private static modelSerialization(config: HttpRequestConfig) {
		if (config.data instanceof VueModel.Entity) {
			config.data = config.data.serialize();
		}
		return config;
	}

	protected async transformServiceRequestOptions(options: ServiceRequestOptions) {
		const config = BaseService.modelSerialization({ ...options });
		config.url = options.endpoint;
		config.params = config.params || {};
		config.headers = {
			'X-Requested-With': 'XMLHttpRequest',
			...config.headers
		};

		if (this.sessionToken)
			config.headers['X-SessionToken'] = this.sessionToken;

		if (options.passive === true)
			config.headers['X-Passive-Request'] = 1;

		return config;
	}

	protected async sendRequest(config: HttpRequestConfig): Promise<HttpResponse> {
		if (config.onUploadProgress)
			return this.sendXmlHttpRequest(config);

		return this.sendFetchRequest(config);
	}

	protected transformResponse(response: HttpResponse) {
		this.receiveServerTime(response);
		if (location.href.startsWith(getSiteUrl()))
			this.receiveSessionToken(response);
		return response;
	}

	async serviceRequest(options: ServiceRequestOptions | string): Promise<ServiceRequestResult> {
		const config = await this.transformServiceRequestOptions(typeof options === 'string'
			? { endpoint: options, method: 'get' }
			: options);

		const result: ServiceRequestResult = {};

		// Handle the response
		try {
			result.response = this.transformResponse(await this.sendRequest(config));
		}
		// Handle the error
		catch (err: unknown) {
			if (err instanceof RequestCancelledError || isHttpError(err)) {
				result.error = this.transformErrorFromResponse(err);
			}
			else {
				result.error = ensureError(err);
			}
		}

		return result;
	}

	private transformErrorFromResponse(err: RequestCancelledError | HttpError): ServiceRequestError {
		if (err instanceof RequestCancelledError)
			return err;

		// Check for a failure that returns a response of type object, i.e. 'JsonErrorInfo'
		if (err.response && err.response.data && isJsonErrorInfo(err.response.data))
			return new CustomResponseError(err.response.data, err.response.status, err.response.statusText, err);

		return err;
	}

	private async sendFetchRequest(config: HttpRequestConfig): Promise<HttpResponse> {
		const responseConfig = { ...config, baseURL: config.baseURL || this.baseUrl };
		const signal = this.createRequestSignal(responseConfig);
		const headers = new Headers();
		for (const [key, value] of Object.entries(responseConfig.headers || {})) {
			headers.set(key, String(value));
		}

		const body = this.createRequestBody(responseConfig, headers);

		try {
			const response = await fetch(this.getUri(responseConfig), {
				method: (responseConfig.method || 'GET').toUpperCase(),
				headers,
				body,
				signal
			});

			const serviceResponse = await this.createResponse(response, responseConfig);
			if (!response.ok)
				throw new HttpError(response.statusText || 'Request failed', responseConfig, serviceResponse);

			return serviceResponse;
		}
		catch (err) {
			if (err instanceof HttpError)
				throw err;
			if (err instanceof DOMException && err.name === 'AbortError')
				throw new RequestCancelledError(err);
			throw new HttpError('Network Error', responseConfig, undefined, 'ERR_NETWORK');
		}
	}

	private async sendXmlHttpRequest(config: HttpRequestConfig): Promise<HttpResponse> {
		const responseConfig = { ...config, baseURL: config.baseURL || this.baseUrl };
		const method = (responseConfig.method || 'GET').toUpperCase();
		const url = this.getUri(responseConfig);

		return new Promise<HttpResponse>((resolve, reject) => {
			const xhr = new XMLHttpRequest();
			xhr.open(method, url, true);
			const body = this.createXmlHttpRequestBody(responseConfig);

			for (const [key, value] of Object.entries(responseConfig.headers || {})) {
				xhr.setRequestHeader(key, String(value));
			}

			if (responseConfig.responseType === 'blob')
				xhr.responseType = 'blob';

			const abort = () => {
				xhr.abort();
				reject(new RequestCancelledError());
			};

			if (responseConfig.signal) {
				if (responseConfig.signal.aborted) {
					abort();
					return;
				}
				responseConfig.signal.addEventListener('abort', abort, { once: true });
			}

			xhr.upload.onprogress = event => {
				if (!responseConfig.onUploadProgress)
					return;

				const total = event.lengthComputable ? event.total : 0;
				responseConfig.onUploadProgress({
					loaded: event.loaded,
					total,
					percent: total > 0 ? event.loaded / total * 100 : 0
				});
			};

			xhr.onerror = () => reject(new HttpError('Network Error', responseConfig, undefined, 'ERR_NETWORK'));
			xhr.onabort = () => reject(new RequestCancelledError());
			xhr.onload = () => {
				const serviceResponse = this.createXmlHttpResponse(xhr, responseConfig);
				if (xhr.status >= 200 && xhr.status < 300)
					resolve(serviceResponse);
				else
					reject(new HttpError(xhr.statusText || 'Request failed', responseConfig, serviceResponse));
			};

			xhr.send(body);
		});
	}

	private createRequestBody(config: HttpRequestConfig, headers: Headers) {
		if (config.data == null)
			return undefined;

		if (config.data instanceof FormData || config.data instanceof Blob || typeof config.data === 'string')
			return config.data;

		if (!headers.has('Content-Type'))
			headers.set('Content-Type', 'application/json; charset=utf-8');

		return JSON.stringify(config.data);
	}

	private createRequestSignal(config: HttpRequestConfig) {
		if (!config.timeout)
			return config.signal;

		const controller = new AbortController();
		const timeoutId = window.setTimeout(() => controller.abort(), config.timeout);
		const abort = () => controller.abort();

		config.signal?.addEventListener('abort', abort, { once: true });
		controller.signal.addEventListener('abort', () => {
			window.clearTimeout(timeoutId);
			config.signal?.removeEventListener('abort', abort);
		}, { once: true });

		return controller.signal;
	}

	private createXmlHttpRequestBody(config: HttpRequestConfig) {
		if (config.data == null)
			return null;
		if (config.data instanceof FormData || config.data instanceof Blob || typeof config.data === 'string')
			return config.data;
		config.headers = config.headers || {};
		if (!config.headers['Content-Type'])
			config.headers['Content-Type'] = 'application/json; charset=utf-8';
		return JSON.stringify(config.data);
	}

	private async createResponse(response: Response, config: HttpRequestConfig): Promise<HttpResponse> {
		return {
			data: await this.readResponseBody(response, config.responseType),
			status: response.status,
			statusText: response.statusText,
			headers: this.normalizeHeaders(response.headers),
			config
		};
	}

	private createXmlHttpResponse(xhr: XMLHttpRequest, config: HttpRequestConfig): HttpResponse {
		return {
			data: this.readXmlHttpResponseBody(xhr, config.responseType),
			status: xhr.status,
			statusText: xhr.statusText,
			headers: this.normalizeRawHeaders(xhr.getAllResponseHeaders()),
			config
		};
	}

	private async readResponseBody(response: Response, responseType: HttpRequestConfig['responseType']) {
		if (response.status === 204)
			return null;
		if (responseType === 'blob')
			return response.blob();

		const contentType = response.headers.get('content-type') || '';
		if (responseType === 'text')
			return response.text();
		if (contentType.includes('application/json') || contentType.includes('+json'))
			return response.json();

		return response.text();
	}

	private readXmlHttpResponseBody(xhr: XMLHttpRequest, responseType: HttpRequestConfig['responseType']) {
		if (xhr.status === 204)
			return null;
		if (responseType === 'blob')
			return xhr.response;

		const contentType = xhr.getResponseHeader('content-type') || '';
		if (responseType === 'text')
			return xhr.responseText;
		if (contentType.includes('application/json') || contentType.includes('+json')) {
			try {
				return JSON.parse(xhr.responseText || 'null');
			}
			catch (e) {
				return xhr.responseText;
			}
		}

		return xhr.responseText;
	}

	private normalizeHeaders(headers: Headers): Record<string, string> {
		const normalized: Record<string, string> = {};
		headers.forEach((value, key) => {
			normalized[key.toLowerCase()] = value;
		});
		return normalized;
	}

	private normalizeRawHeaders(rawHeaders: string): Record<string, string> {
		const normalized: Record<string, string> = {};
		for (const line of rawHeaders.split(/\r?\n/)) {
			if (!line)
				continue;
			const separatorIndex = line.indexOf(':');
			if (separatorIndex < 0)
				continue;
			const key = line.substring(0, separatorIndex).trim().toLowerCase();
			normalized[key] = line.substring(separatorIndex + 1).trim();
		}
		return normalized;
	}

	/**
	 * Determine the server time offset relative to the client time during posts
	 */
	private receiveServerTime(res: HttpResponse<any>) {
		const serverTime = res.headers['x-server-time'];
		if (serverTime) {
			try {
				FormsModel.config.serverTimeOffset = new Date(serverTime).getTime() - new Date().getTime();
			}
			catch (e) { }
		}
		return res;
	}

	private receiveSessionToken(res: HttpResponse<any>) {
		const sessionToken = res.headers['x-sessiontoken'];
		if (sessionToken) {
			try {
				const event = new CustomEvent<string>('received-session-token', { detail: sessionToken }) as CustomEvent<string> & { token: string };
				event.token = sessionToken;
				document.dispatchEvent(event);
			}
			catch (e) { }
		}
		return res;
	}
}

export class ServiceWithSession extends BaseService {
	session: FormSession;

	constructor(session: FormSession) {
		super(session.apiKey, session.token);
		this.session = session;
	}

	protected getValidationHeaders(): Record<string, string> | null {
		let validationMethod = null;
		let validationToken = null;
		if (this.session && this.session.botValidation && this.session.botValidation.method && this.session.botValidation.token) {
			validationMethod = this.session.botValidation.method;
			validationToken = this.session.botValidation.token;
		}

		return validationMethod && validationToken ? { [validationMethod]: validationToken } : null;
	}

	protected transformResponse(response: HttpResponse) {
		const res = super.transformResponse(response);
		this.receiveAccessToken(res);
		return res;
	}

	private receiveAccessToken(res: HttpResponse<any>) {
		// capture fresh accessToken
		if (res.data && res.data['accessToken'])
			this.session.accessToken = res.data['accessToken'];
		return res;
	}

	protected async transformServiceRequestOptions(options: ServiceRequestOptions) {
		const config = await super.transformServiceRequestOptions(options);
		config.headers = config.headers || {};
		if (this.session.organizationId)
			config.headers['X-Organization'] = this.session.organizationId;
		const validationHeaders = this.getValidationHeaders();
		if (validationHeaders)
			Object.assign(config.headers, validationHeaders);
		return config;
	}

	parseError(error: ServiceRequestError): Error {
		const responseData = isHttpError(error) ? error.response?.data : undefined;

		if (responseData === 'captcha')
			return new CaptchaError(error);
		else if (hasErrorField(responseData) && responseData.error === 'Invalid Shared Link')
			return new InvalidSharedLinkError('Invalid Shared Link', error);
		else if (hasErrorField(responseData) && responseData.error === 'Public Links Disabled')
			return new PublicLinksDisabledError(error);
		else if (hasErrorField(responseData) && responseData.error === 'Stripe PaymentIntent Failure')
			return new StripePaymentIntentError(error);
		else if (hasErrorField(responseData) && responseData.error === 'View As User')
			return new ViewAsUserError(error);
		else if (isHttpError(error) && error.response && error.response.status === 500) {
			const errorHeading = this.session.getResource('entry-submission-error-heading');
			ToastMessage(this.session.formId, {
				type: 'error',
				message: errorHeading || 'Internal server error'
			});
			return new InternalServerError(error);
		}
		else
			return this.parseSvcError(error);
	}

	parseSvcError(error: ServiceRequestError): Error {
		if (error instanceof CustomResponseError) {
			if (error.type === 'Forbidden')
				return new ForbiddenError(error);
			else if (error.data?.error === 'Invalid Shared Link')
				return new InvalidSharedLinkError(error.data.message, error);
			else if (error.data?.error === 'Public Links Disabled')
				return new PublicLinksDisabledError(error);
			else if (error.data?.error === 'Stripe PaymentIntent Failure')
				return new StripePaymentIntentError(error);
		}
		return error;
	}
}
